import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { CacheKeyConstants, CacheService, isNullOrUndefined } from '@uzurv/core';
// eslint-disable-next-line import/order
import { UZURV_REST_CONFIGURATION, UzurvRestConfiguration, UzurvRestService } from '@uzurv/http';
import { Deserialize, Serialize } from 'cerialize';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  Organization,
  OrganizationPaymentMethod,
  OrganizationRiderPool,
  OrganizationServiceLevel,
  OrganizationType,
  RideLimitPeriod,
  RideMetadataField,
  TransitProgram,
  TransitProgramInfo
} from '../../models';

@Injectable({
  providedIn: 'root'
})
export class OrganizationService extends UzurvRestService {
  readonly resourceUrl = 'organizations/';

  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    protected domSanitizer: DomSanitizer,
    private cacheService: CacheService
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /**
   * Searches for organizations on a searchString
   *
   * @param searchString
   * @param pageNumber
   * @param pageSize
   * @permission organization_search
   */
  searchOrganizations(searchString: string, pageNumber = 0, pageSize = 7): Observable<Array<Organization>> {
    return this.get<Array<Organization>>(`search/${this.resourceUrl}`, {
      searchString: searchString,
      pageNumber: pageNumber,
      pageSize: pageSize
    }).pipe(map(response => Deserialize(response, Organization)));
  }

  /**
   * Finds an organization by ID
   *
   * @param id
   */
  findById(organizationId: number): Observable<Organization> {
    return this.get<TransitProgram>(`${this.resourceUrl}${organizationId}`).pipe(map(response => Deserialize(response, Organization)));
  }

  /**
   * Finds the nearest transit program in the supplied organization to the supplied location
   *
   * @param organizationId
   * @param pickUpLocationId
   * @param dropOffLocationId
   * @permission organization_read
   */
  findNearestTransitProgramByLocations(
    organizationId: number,
    pickUpLocationId: number,
    dropOffLocationId
  ): Observable<TransitProgramInfo> {
    return this.get<TransitProgramInfo>(`${this.resourceUrl}${organizationId}/transitPrograms/`, {
      pickUpLocationId: pickUpLocationId,
      dropOffLocationId: dropOffLocationId
    }).pipe(map(response => Deserialize(response, TransitProgramInfo)));
  }

  /**
   * @param id
   * @permission organization_read
   */
  listRideMetadataFieldsForTransitProgram(organizationId: number, transitProgramId: number): Observable<Array<RideMetadataField>> {
    return this.get<Array<RideMetadataField>>(
      `${this.resourceUrl}${organizationId}/transitPrograms/${transitProgramId}/rideMetadataFields/`
    ).pipe(map(response => Deserialize(response, RideMetadataField)));
  }
  /**
   * Gets a list of all the organizations
   *
   * @param pageNumber
   * @param pageSize
   * @permission organization_read
   */
  listAll(pageNumber = 0, pageSize = 1000, overrideCache = false): Observable<Array<Organization>> {
    if (!overrideCache) {
      const cachedData = Deserialize(JSON.parse(this.cacheService.getCachedData(CacheKeyConstants.ORGANIZATIONS)), Organization);
      if (!isNullOrUndefined(cachedData)) {
        return of(cachedData);
      }
    }
    return this.get<Array<Organization>>(this.resourceUrl, { pageNumber: pageNumber, pageSize: pageSize }).pipe(
      map((response: Array<Organization>) => {
        this.cacheService.cacheData(CacheKeyConstants.ORGANIZATIONS, JSON.stringify(Serialize(response)));
        return Deserialize(response, Organization);
      })
    );
  }

  /**
   * Gets a list of transit programs in an organization
   *
   * @param id
   * @permission organization_management
   */
  listTransitProgramsByOrganizationId(id: number): Observable<Array<TransitProgram>> {
    return this.get<Array<TransitProgram>>(`${this.resourceUrl}${id}/transitPrograms/`).pipe(
      map(response => Deserialize(response, TransitProgram))
    );
  }

  /**
   * Returns the allowed transfer types for an organization
   *
   * @param id
   * @permission organization_management
   */
  listOrganizationServiceLevels(id: number): Observable<Array<OrganizationServiceLevel>> {
    return this.get<Array<OrganizationServiceLevel>>(`${this.resourceUrl}${id}/serviceLevels/`).pipe(
      map(response => Deserialize(response, OrganizationServiceLevel))
    );
  }

  /**
   * Returns rider pools by organization id.
   */
  listRiderPoolsByOrganizationId(id: number): Observable<Array<OrganizationRiderPool>> {
    return this.get<Array<OrganizationRiderPool>>(`${this.resourceUrl}${id}/organizationRiderPools/`).pipe(
      map(response => Deserialize(response, OrganizationRiderPool))
    );
  }

  /**
   * @permission organization_read
   */
  listRideLimitPeriods(): Observable<Array<RideLimitPeriod>> {
    return this.get<Array<RideLimitPeriod>>(`${this.resourceUrl}rideLimitPeriods/`).pipe(
      map(response => Deserialize(response, RideLimitPeriod))
    );
  }

  /**
   * @param organization
   * @permission organization_create
   */
  createOrganization(organization: Organization): Observable<Organization> {
    return this.post<Organization>(this.resourceUrl, Serialize(organization)).pipe(map(response => Deserialize(response, Organization)));
  }

  /**
   * @param id
   * @param organization
   * @permission organization_management
   */
  update(id: number, organization: Organization): Observable<Organization> {
    return this.put<Organization>(`${this.resourceUrl}${id}`, Serialize(organization)).pipe(
      map(response => Deserialize(response, Organization))
    );
  }

  /**
   * @permission organization_read
   */
  listOrganizationTypes(): Observable<Array<OrganizationType>> {
    return this.get<Array<OrganizationType>>(`${this.resourceUrl}organizationTypes/`).pipe(
      map(response => Deserialize(response, OrganizationType))
    );
  }

  /************************************* Rider Payment Collection ***********************/

  /**
   * @param organizationId
   */
  listPaymentMethodsByOrganizationId(organizationId: number): Observable<Array<OrganizationPaymentMethod>> {
    return this.get<Array<OrganizationPaymentMethod>>(`${this.resourceUrl}${organizationId}/paymentMethods/`).pipe(
      map(response => Deserialize(response, OrganizationPaymentMethod))
    );
  }

  /**
   * @param organizationId
   * @param organizationPaymentMethod
   */
  addPaymentMethod(organizationId: number, organizationPaymentMethod: OrganizationPaymentMethod): Observable<OrganizationPaymentMethod> {
    return this.post<OrganizationPaymentMethod>(
      `${this.resourceUrl}${organizationId}/paymentMethods/`,
      Serialize(organizationPaymentMethod)
    ).pipe(map(response => Deserialize(response, OrganizationPaymentMethod)));
  }

  /**
   * @param organizationId
   * @param organizationPaymentMethodId
   */
  removeOrganizationPaymentMethod(organizationId: number, organizationPaymentMethodId: number): Observable<boolean> {
    return this.delete(`${this.resourceUrl}${organizationId}/paymentMethods/${organizationPaymentMethodId}`);
  }
}
